import { SavedOffer, AppSettings, QuoteSections } from '../types';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { companyPresets, printLanguages } from '../config';
import { makeDefaultSections } from '../config/quoteSectionDefaults';
import {
  formatCurrency,
  formatDate,
  calculateLaborCost,
  calculateAccommodationCost,
  calculateCallOutFee,
  calculatePartCost
} from '../utils';

interface PrintViewProps {
  offer: SavedOffer;
  settings: AppSettings;
  onClose: () => void;
  onCreateWorkOrder?: () => void;
  onUpdateOffer?: (updates: Partial<SavedOffer>) => void;
}

const PrintView = ({ offer, settings, onClose, onCreateWorkOrder, onUpdateOffer }: PrintViewProps) => {
  const { t: tUI, i18n } = useTranslation();
  // State for print language, priority: offer setting -> global setting -> default 'en'
  const [printLanguage, setPrintLanguage] = useState(
    offer.settings.printLanguage || settings.printLanguage || 'en'
  );
  // State for optional project ID
  const [projectID, setProjectID] = useState('');
  // State for custom document number override
  const [customDocumentNumber, setCustomDocumentNumber] = useState('');
  // State for payment terms override
  const [paymentTermsDays, setPaymentTermsDays] = useState(
    (offer.settings as any).paymentTermsDays || settings.paymentTermsDays || 14
  );
  // State for validity period (days)
  const [validityDays, setValidityDays] = useState(offer.validityDays || 30);
  // State for delivery terms (free text)
  const [deliveryTerms, setDeliveryTerms] = useState(offer.deliveryTerms || '');
  // State to show/hide delivery terms input
  const [showDeliveryTerms, setShowDeliveryTerms] = useState(!!offer.deliveryTerms);
  // Price display options
  const [hideLinePrices, setHideLinePrices] = useState(offer.hideLinePrices ?? false);
  const [customSubtotal, setCustomSubtotal] = useState<number | null>(offer.customSubtotal ?? null);
  const [vatEnabled, setVatEnabled] = useState(offer.vatEnabled ?? true);
  // Configurable print sections (technical/scope/terms/attachments/signature)
  const [sections, setSections] = useState<QuoteSections>(offer.sections || {});

  const sectionKeys = ['technical', 'scope', 'terms', 'attachments', 'signature'] as const;

  const toggleSection = (key: (typeof sectionKeys)[number]) => {
    setSections((prev) => {
      const next: QuoteSections = { ...prev };
      const current = prev[key];
      if (current?.enabled) {
        next[key] = { ...current, enabled: false } as never;
      } else if (current) {
        next[key] = { ...current, enabled: true } as never;
      } else {
        const defaults = makeDefaultSections(settings, i18n.getFixedT(printLanguage));
        next[key] = { ...defaults[key]!, enabled: true } as never;
      }
      return next;
    });
  };

  // Track initial mount to avoid persisting on first render
  const isInitialMount = useRef(true);

  // Persist price display options back to storage when they change
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    onUpdateOffer?.({
      hideLinePrices,
      customSubtotal,
      vatEnabled,
      sections,
    });
  }, [hideLinePrices, customSubtotal, vatEnabled, sections]);

  const t = i18n.getFixedT(printLanguage);
  
  // Use custom document number if provided, otherwise use the original
  const displayDocumentNumber = customDocumentNumber || offer.documentNumber;
  
  // Map print language to country code for company details
  const langToCountry: Record<string, keyof typeof companyPresets> = {
    fi: 'fi',
    sv: 'se',
    no: 'no',
    da: 'dk', // Danish uses Danish company (Halton A/S)
    pl: 'pl',
    nl: 'nl',
    de: 'de',
    fr: 'fr',
    en: 'gb', // Default English to UK or generic
  };

  // Get currency country from selected print language
  const currencyCountry = langToCountry[printLanguage] || settings.companyCountry;

  const company = companyPresets[currencyCountry] || companyPresets[settings.companyCountry];

  const handlePrint = () => {
    window.print();
  };

  // Computed effective totals based on price display options
  const effectiveSubtotal = customSubtotal ?? offer.totals.subtotal;
  const effectiveVatPercent = vatEnabled ? offer.totals.vatPercent : 0;
  const effectiveVat = vatEnabled ? effectiveSubtotal * (offer.totals.vatPercent / 100) : 0;
  const effectiveTotal = effectiveSubtotal + effectiveVat;

  return (
    <div className="print-view">
      {/* Print Controls - Hidden when printing */}
      <div className="print-controls no-print">
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button className="btn btn-secondary" onClick={onClose}>
            ← {tUI('common.back')}
          </button>

          {/* Language Selector */}
          <select
            value={printLanguage}
            onChange={(e) => setPrintLanguage(e.target.value as any)}
            className="form-select"
            style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd' }}
            title={t('settings.print.language')}
          >
            {printLanguages.map(lang => (
              <option key={lang.code} value={lang.code}>
                {lang.nativeName}
              </option>
            ))}
          </select>

          {/* Payment Terms Dropdown */}
          <select
            value={paymentTermsDays}
            onChange={(e) => setPaymentTermsDays(Number(e.target.value))}
            className="form-select"
            style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd' }}
            title={t('print.settings.paymentTerms')}
          >
            <option value={14}>14 {t('print.settings.days')} net</option>
            <option value={21}>21 {t('print.settings.days')} net</option>
            <option value={30}>30 {t('print.settings.days')} net</option>
          </select>

          {/* Project ID Input */}
          <input
            type="text"
            value={projectID}
            onChange={(e) => setProjectID(e.target.value)}
            placeholder={t('print.settings.projectID')}
            className="form-input"
            style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd', width: '120px' }}
          />

          {/* Custom Document Number Input */}
          <input
            type="text"
            value={customDocumentNumber}
            onChange={(e) => setCustomDocumentNumber(e.target.value)}
            placeholder={t('print.header.documentNumber')}
            className="form-input"
            style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd', width: '140px' }}
          />

          {/* Validity Period Selector */}
          <select
            value={validityDays}
            onChange={(e) => setValidityDays(Number(e.target.value))}
            className="form-select"
            style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd' }}
            title={t('print.settings.validityPeriod')}
          >
            <option value={14}>14 {t('print.settings.days')} {t('print.settings.valid')}</option>
            <option value={21}>21 {t('print.settings.days')} {t('print.settings.valid')}</option>
            <option value={30}>30 {t('print.settings.days')} {t('print.settings.valid')}</option>
            <option value={60}>60 {t('print.settings.days')} {t('print.settings.valid')}</option>
            <option value={90}>90 {t('print.settings.days')} {t('print.settings.valid')}</option>
          </select>

          {/* Delivery Terms Toggle */}
          <button
            className={`btn btn-sm ${showDeliveryTerms ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setShowDeliveryTerms(!showDeliveryTerms)}
            style={{ padding: '6px 10px', fontSize: '0.85rem' }}
            title={t('print.settings.deliveryTerms')}
          >
            📦 {t('print.settings.deliveryTerms')}
          </button>

          {/* Hide Line Prices Toggle */}
          <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.85rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={hideLinePrices}
              onChange={(e) => setHideLinePrices(e.target.checked)}
            />
            {t('print.settings.hideLinePrices')}
          </label>

          {/* VAT Toggle */}
          <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.85rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={vatEnabled}
              onChange={(e) => setVatEnabled(e.target.checked)}
            />
            {t('print.settings.vatToggle')}
          </label>

          {/* Custom Subtotal Input */}
          <input
            type="number"
            min={0}
            value={customSubtotal ?? ''}
            onChange={(e) => setCustomSubtotal(e.target.value ? Number(e.target.value) : null)}
            placeholder={t('print.settings.customSubtotal') + ' (€)'}
            className="form-input"
            style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd', width: '160px' }}
          />
        </div>

        {/* Delivery Terms Input (shown when toggled) */}
        {showDeliveryTerms && (
          <div style={{ marginTop: '10px' }}>
            <input
              type="text"
              value={deliveryTerms}
              onChange={(e) => setDeliveryTerms(e.target.value)}
              placeholder={t('print.settings.deliveryTermsPlaceholder')}
              className="form-input"
              style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd', width: '100%', maxWidth: '400px' }}
            />
          </div>
        )}

        {onCreateWorkOrder && (
          <button className="btn btn-secondary" onClick={onCreateWorkOrder}>
            📋 {tUI('print.createWorkOrder')}
          </button>
        )}
        <button className="btn btn-primary" onClick={handlePrint}>
          🖨️ {tUI('common.printPdf')}
        </button>
        <span className="print-tip" style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '12px' }}>
          💡 {t('print.tip.saveasPdf')}
        </span>
      </div>

      {/* Section toggles (configurable quote sections) */}
      <div className="print-controls no-print" data-testid="print-section-toggles" style={{ flexWrap: 'wrap', gap: '10px' }}>
        <strong style={{ fontSize: '0.8rem' }}>{tUI('calculator.sections.title')}:</strong>
        {sectionKeys.map((key) => (
          <label key={key} style={{ fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <input
              type="checkbox"
              checked={!!sections[key]?.enabled}
              onChange={() => toggleSection(key)}
              data-testid={`print-toggle-${key}`}
            />
            {tUI(`calculator.sections.${key}`)}
          </label>
        ))}
      </div>

      {/* Print Document */}
      <div className="print-document">
        {/* Header */}
        <header className="print-header">
          <div className="print-header-left">
            <img src="/halton-logo.png" alt="Halton" className="print-logo" style={{ height: '32px', marginBottom: '8px' }} />
            <div className="company-info">
              {company.name}<br />
              {company.address}<br />
              {company.postalCode} {company.city}<br />
              {t('print.company.vat')}: {company.vatId}
            </div>
          </div>

          <div className="print-header-right">
            <h1 className="document-title">{t('print.document.quote')}</h1>
            <div className="document-meta">
              <div className="meta-row">
                <span className="meta-label">{t('print.header.documentNumber')}:</span>
                <span className="meta-value">{displayDocumentNumber}</span>
              </div>
              {offer.jobNumber && (
                <div className="meta-row">
                  <span className="meta-label">{t('print.workOrder.jobNumber')}:</span>
                  <span className="meta-value" style={{ fontWeight: 'bold' }}>{offer.jobNumber}</span>
                </div>
              )}
              <div className="meta-row">
                <span className="meta-label">{t('print.header.date')}:</span>
                <span className="meta-value">{formatDate(offer.createdAt, printLanguage)}</span>
              </div>
              {projectID && (
                <div className="meta-row">
                  <span className="meta-label">{t('print.header.projectID')}:</span>
                  <span className="meta-value">{projectID}</span>
                </div>
              )}
              <div className="meta-row">
                <span className="meta-label">{t('print.header.validUntil')}:</span>
                <span className="meta-value">{validityDays} {t('print.header.days')}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Customer Information */}
        <section className="print-section" style={{ marginBottom: '30px' }}>
          <div>
            <h2 className="print-section-title">{t('print.customer.title')}</h2>
            <div className="customer-info">
              {offer.customerName && <strong>{offer.customerName}</strong>}
              {offer.customerAddress && <span>{offer.customerAddress}</span>}
            </div>
          </div>
        </section>

        {offer.customerNotes && (
          <div className="customer-notes">
            {offer.customerNotes}
          </div>
        )}

        {/* Pricing Table */}
        <section className="print-section">
          <table className="print-table">
            <thead>
              <tr>
                <th style={{ width: '50%' }}>{t('print.pricing.description')}</th>
                <th style={{ width: '15%', textAlign: 'center' }}>{t('print.pricing.quantity')}</th>
                <th style={{ width: '17%', textAlign: 'right' }}>{t('print.pricing.unitPrice')}</th>
                <th style={{ width: '18%', textAlign: 'right' }}>{t('print.pricing.total')}</th>
              </tr>
            </thead>
            <tbody>
              {/* Custom Line Items */}
              {offer.customLines.length > 0 && offer.customLines.map((line, index) => {
                const discount = line.discountPercent || 0;
                const discountedPrice = line.price * (1 - discount / 100);
                return (
                  <tr key={`custom-${index}`}>
                    <td>
                      <strong>{line.name}</strong>
                      {line.description && (
                        <span className="item-desc">{line.description}</span>
                      )}
                      {discount > 0 && (
                        <span className="item-desc"> (-{discount}% {t('print.travel.discount')})</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>1 {t('print.pricing.pcs')}</td>
                    <td style={{ textAlign: 'right' }}>{hideLinePrices ? '—' : formatCurrency(line.price, currencyCountry)}</td>
                    <td style={{ textAlign: 'right' }}>{hideLinePrices ? '—' : formatCurrency(discountedPrice, currencyCountry)}</td>
                  </tr>
                );
              })}

              {/* Travel Items - Split into km cost and time cost rows */}
              {offer.travelItems.length > 0 && offer.travelItems.map((item, index) => {
                if (item.mode === 'fixed') {
                  // Fixed price mode - single row
                  const fixedCost = item.fixedPrice * (1 - (item.discountPercent || 0) / 100);
                  return (
                    <tr key={`travel-${index}`}>
                      <td>
                        <strong>{t('print.travel.title')} {offer.travelItems.length > 1 ? `#${index + 1}` : ''}</strong>
                        <span className="item-desc">
                          {t('print.travel.fixedCharge')}: {formatCurrency(item.fixedPrice, currencyCountry)}
                          {item.discountPercent > 0 && ` (-${item.discountPercent}% ${t('print.travel.discount')})`}
                        </span>
                        {item.description && (
                          <span className="item-desc" style={{ display: 'block', fontStyle: 'italic' }}>{item.description}</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>1</td>
                      <td style={{ textAlign: 'right' }}>{hideLinePrices ? '—' : formatCurrency(fixedCost, currencyCountry)}</td>
                      <td style={{ textAlign: 'right' }}>{hideLinePrices ? '—' : formatCurrency(fixedCost, currencyCountry)}</td>
                    </tr>
                  );
                } else {
                  // Per-km mode - separate rows for km and time costs
                  const kmCost = item.distance * item.distanceRate * (item.tripMultiplier || 1) * (1 - (item.discountPercent || 0) / 100);
                  const timeCost = item.travelTime * item.timeRate * (item.tripMultiplier || 1) * (1 - (item.discountPercent || 0) / 100);
                  const travelLabel = offer.travelItems.length > 1 ? ` #${index + 1}` : '';
                  const isRoundTrip = (item.tripMultiplier || 1) === 2;
                  
                  return (
                    <>
                      {/* Distance cost row */}
                      {item.distance > 0 && (
                        <tr key={`travel-km-${index}`}>
                          <td>
                            <strong>{t('print.travel.kmCost')}{travelLabel}</strong>
                            {isRoundTrip && <span className="item-desc" style={{ fontWeight: 'bold', color: '#1a73e8' }}> ({t('print.travel.roundTrip')})</span>}
                            <span className="item-desc">
                              {t('print.travel.kmSummary', { distance: item.distance, rate: item.distanceRate })}
                              {item.discountPercent > 0 && ` (-${item.discountPercent}% ${t('print.travel.discount')})`}
                            </span>
                            {item.description && (
                              <span className="item-desc" style={{ display: 'block', fontStyle: 'italic' }}>{item.description}</span>
                            )}
                          </td>
                          <td style={{ textAlign: 'center' }}>{item.distance} km</td>
                          <td style={{ textAlign: 'right' }}>{hideLinePrices ? '—' : `${formatCurrency(item.distanceRate, currencyCountry)}/km`}</td>
                          <td style={{ textAlign: 'right' }}>{hideLinePrices ? '—' : formatCurrency(kmCost, currencyCountry)}</td>
                        </tr>
                      )}
                      {/* Time cost row */}
                      {item.travelTime > 0 && (
                        <tr key={`travel-time-${index}`}>
                          <td>
                            <strong>{t('print.travel.timeCost')}{travelLabel}</strong>
                            {isRoundTrip && <span className="item-desc" style={{ fontWeight: 'bold', color: '#1a73e8' }}> ({t('print.travel.roundTrip')})</span>}
                            <span className="item-desc">
                              {t('print.travel.timeSummary', { time: item.travelTime, rate: item.timeRate })}
                              {item.discountPercent > 0 && ` (-${item.discountPercent}% ${t('print.travel.discount')})`}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center' }}>{item.travelTime} h</td>
                          <td style={{ textAlign: 'right' }}>{hideLinePrices ? '—' : `${formatCurrency(item.timeRate, currencyCountry)}/h`}</td>
                          <td style={{ textAlign: 'right' }}>{hideLinePrices ? '—' : formatCurrency(timeCost, currencyCountry)}</td>
                        </tr>
                      )}
                    </>
                  );
                }
              })}

              {/* Labor Items */}
              {offer.workItems.length > 0 && offer.workItems.map((item, index) => {
                const laborCost = calculateLaborCost(item);
                const callOutFee = calculateCallOutFee(item);
                const accommodationCost = calculateAccommodationCost(item, settings);
                const surcharges = [];
                if (item.nightSunday) surcharges.push('+50% ' + t('print.labor.nightSurcharge'));
                if (item.urgent) surcharges.push('+50% ' + t('print.labor.urgentSurcharge'));

                return (
                  <>
                    <tr key={`labor-${index}`}>
                      <td>
                        <strong>{t('print.labor.title')} {offer.workItems.length > 1 ? `#${index + 1}` : ''}</strong>
                        <span className="item-desc">
                          {item.technicians} {t('print.labor.technicians')} × {item.hours} {t('print.pricing.hours')} @ {formatCurrency(item.hourlyRate, currencyCountry)}/h
                          {surcharges.length > 0 && ` (${surcharges.join(', ')})`}
                          {(item.discountPercent || 0) > 0 && ` (-${item.discountPercent}% ${t('print.travel.discount')})`}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>{item.hours}</td>
                      <td style={{ textAlign: 'right' }}>{hideLinePrices ? '—' : formatCurrency(item.hourlyRate * item.technicians, currencyCountry)}</td>
                      <td style={{ textAlign: 'right' }}>{hideLinePrices ? '—' : formatCurrency(laborCost, currencyCountry)}</td>
                    </tr>

                    {callOutFee > 0 && (
                      <tr key={`callout-${index}`}>
                        <td style={{ paddingLeft: '20px' }}>{t('print.labor.callOutFee')}</td>
                        <td style={{ textAlign: 'center' }}>1</td>
                        <td style={{ textAlign: 'right' }}>{hideLinePrices ? '—' : formatCurrency(callOutFee, currencyCountry)}</td>
                        <td style={{ textAlign: 'right' }}>{hideLinePrices ? '—' : formatCurrency(callOutFee, currencyCountry)}</td>
                      </tr>
                    )}

                    {accommodationCost > 0 && (
                      <tr key={`accommodation-${index}`}>
                        <td style={{ paddingLeft: '20px' }}>
                          <strong>{t('print.accommodation.title')}</strong>
                          <span className="item-desc">
                            {item.accommodationNights > 0 && `${item.accommodationNights} ${t('print.pricing.nights')}`}
                            {item.accommodationNights > 0 && item.perDiemDays > 0 && ' + '}
                            {item.perDiemDays > 0 && `${item.perDiemDays} ${t('print.accommodation.perDiem')}`}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>1</td>
                        <td style={{ textAlign: 'right' }}>{hideLinePrices ? '—' : formatCurrency(accommodationCost, currencyCountry)}</td>
                        <td style={{ textAlign: 'right' }}>{hideLinePrices ? '—' : formatCurrency(accommodationCost, currencyCountry)}</td>
                      </tr>
                    )}
                  </>
                );
              })}

              {/* Parts */}
              {offer.parts.length > 0 && (
                <>
                  <tr className="section-header-row">
                    <td colSpan={4}><strong>{t('print.parts.title')}</strong></td>
                  </tr>
                  {offer.parts.map((part, index) => {
                    const cost = calculatePartCost(part);
                    const unitPriceWithMarkup = part.unitPrice * (1 + part.markup / 100);
                    return (
                      <tr key={`part-${index}`}>
                        <td style={{ paddingLeft: '20px' }}>
                          {part.description}
                          {(part.discountPercent || 0) > 0 && (
                            <span className="item-desc"> (-{part.discountPercent}% {t('print.travel.discount')})</span>
                          )}
                        </td>
                        <td style={{ textAlign: 'center' }}>{part.quantity} {t('print.pricing.pcs')}</td>
                        <td style={{ textAlign: 'right' }}>{hideLinePrices ? '—' : formatCurrency(unitPriceWithMarkup, currencyCountry)}</td>
                        <td style={{ textAlign: 'right' }}>{hideLinePrices ? '—' : formatCurrency(cost, currencyCountry)}</td>
                      </tr>
                    );
                  })}
                </>
              )}
            </tbody>
          </table>
        </section>

        {/* Totals */}
        <section className="print-totals">
          <div className="totals-table">
            <div className="totals-row">
              <span>{t('print.pricing.subtotal')}</span>
              <span>{formatCurrency(effectiveSubtotal, currencyCountry)}</span>
            </div>
            <div className="totals-row">
              <span>
                {t('print.pricing.vat')} ({effectiveVatPercent.toFixed(1)}%)
              </span>
              <span>{formatCurrency(effectiveVat, currencyCountry)}</span>
            </div>
            {!vatEnabled && (
              <div className="totals-row">
                <span>{t('print.settings.vatDisabled')}</span>
                <span />
              </div>
            )}
            <div className="totals-row total">
              <span>{t('print.pricing.grandTotal')}</span>
              <span>{formatCurrency(effectiveTotal, currencyCountry)}</span>
            </div>
          </div>
        </section>

        {/* Configurable sections (spec Part 1, order 4-9) */}
        {sections.technical?.enabled && sections.technical.rows.length > 0 && (
          <section className="print-section" data-testid="print-section-technical">
            <h2 className="print-section-title">{t('print.sections.technical')}</h2>
            <table className="print-bom-table">
              <thead>
                <tr>
                  <th className="bom-pos">{t('print.sections.techPos')}</th>
                  <th className="bom-code">{t('print.sections.techCode')}</th>
                  <th>{t('print.sections.techDescription')}</th>
                  <th className="bom-qty">{t('print.sections.techQty')}</th>
                </tr>
              </thead>
              <tbody>
                {sections.technical.rows.map((row, i) => (
                  <tr key={i}>
                    <td className="bom-pos">{row.pos}</td>
                    <td className="bom-code">{row.code}</td>
                    <td>{row.description}</td>
                    <td className="bom-qty">{row.qty}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {sections.scope?.enabled && sections.scope.rows.length > 0 && (
          <section className="print-section" data-testid="print-section-scope">
            <h2 className="print-section-title">{t('print.sections.scope')}</h2>
            <table className="print-scope-table">
              <thead>
                <tr>
                  <th>{t('print.sections.scopeParty')}</th>
                  <th>{t('print.sections.scopeContent')}</th>
                </tr>
              </thead>
              <tbody>
                {sections.scope.rows.map((row, i) => (
                  <tr key={i}>
                    <td>{row.party}</td>
                    <td>{row.content}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {sections.terms?.enabled && sections.terms.rows.length > 0 && (
          <section className="print-section" data-testid="print-section-terms">
            <h2 className="print-section-title">{t('print.sections.terms')}</h2>
            <table className="print-param-table">
              <thead>
                <tr>
                  <th>{t('print.sections.termParam')}</th>
                  <th>{t('print.sections.termValue')}</th>
                </tr>
              </thead>
              <tbody>
                {sections.terms.rows.map((row, i) => (
                  <tr key={i}>
                    <td>{row.param}</td>
                    <td>{row.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {sections.attachments?.enabled && sections.attachments.items.length > 0 && (
          <section className="print-section" data-testid="print-section-attachments">
            <h2 className="print-section-title">{t('print.sections.attachments')}</h2>
            <ol className="print-attachments-list">
              {sections.attachments.items.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ol>
          </section>
        )}

        {/* Footer */}
        <footer className="print-footer">
          {deliveryTerms && !sections.terms?.enabled && (
            <div className="delivery-terms" style={{ marginBottom: '10px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
              <strong>{t('print.footer.deliveryTerms')}:</strong> {deliveryTerms}
            </div>
          )}
          {!sections.terms?.enabled && (
            <div className="payment-terms">
              <strong>{t('print.footer.paymentTerms', { days: paymentTermsDays })}</strong>
            </div>
          )}

          {sections.signature?.enabled && (
            <div className="print-signature-block" data-testid="print-section-signature">
              <p className="signature-greeting">{t('print.sections.signatureGreeting')}</p>
              <div className="signature-name">{sections.signature.name}</div>
              {sections.signature.title && (
                <div className="signature-title">
                  {sections.signature.title} · {company.name}
                </div>
              )}
              {(sections.signature.email || sections.signature.phone) && (
                <div className="signature-contact">
                  {[sections.signature.email, sections.signature.phone].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
          )}

          <p className="thank-you">{t('print.footer.thankYou')}</p>
        </footer>
      </div>
    </div>
  );
};

export default PrintView;
